import classNames from "classnames";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { CheckIcon, ClockIcon } from "@icons";
import type { SelectedPreviewEntity } from "./applyCoaTemplatePreviewEntries";
import { ApplyCoaPreviewSelectionCheckbox } from "./ApplyCoaPreviewSelectionCheckbox";
import styles from "./ApplyCoaTemplatePreviewStep.module.scss";

interface Props {
    entry: SelectedPreviewEntity;
    isActive: boolean;
    isEntitySelected: boolean;
    isApplied: boolean;
    isApplying: boolean;
    isDisabled: boolean;
    isReverted: boolean;
    hasCheckbox: boolean;
    // Why this entity can't be selected, when the caller knows. It joins the
    // control's accessible name and its tooltip so "greyed out" is never the
    // only signal.
    disabledReason?: string;
    selectionMode: "apply" | "undo" | "readOnly";
    onSelect: (entityId: number) => void;
    onToggleSelection: (entityId: number) => void;
}

// The row is a plain container so the selection checkbox and the navigation
// control stay siblings: nesting one button inside another breaks keyboard
// traversal and leaves the inner control without an accessible name of its own.
export const ApplyCoaPreviewEntityRow = ({
    entry,
    isActive,
    isEntitySelected,
    isApplied,
    isApplying,
    isDisabled,
    isReverted,
    hasCheckbox,
    disabledReason,
    selectionMode,
    onSelect,
    onToggleSelection,
}: Props) => (
    <div
        className={classNames(styles.entityRow, {
            [styles.selected]: isActive,
        })}
    >
        {selectionMode !== "readOnly" && hasCheckbox && (
            // Native `disabled` is omitted on purpose: a disabled checkbox drops
            // out of the tab order, so a keyboard user cannot reach it to learn
            // why the entity can't be undone. `aria-disabled` plus the guarded
            // click handler convey the state without hiding the control.
            <ApplyCoaPreviewSelectionCheckbox
                label={`Select ${entry.name}`}
                ariaChecked={isEntitySelected}
                isDisabled={isDisabled}
                disabledReason={isDisabled ? disabledReason : undefined}
                onClick={() => onToggleSelection(entry.entityId)}
            >
                <CheckboxIndicator
                    state={isEntitySelected ? "checked" : "unchecked"}
                    disabled={isDisabled}
                />
            </ApplyCoaPreviewSelectionCheckbox>
        )}
        <button
            type="button"
            data-entity-nav="true"
            aria-current={isActive ? "true" : undefined}
            className={styles.entityNavButton}
            onClick={() => onSelect(entry.entityId)}
        >
            <EntityAvatar
                size="xs"
                entity={{
                    name: entry.name,
                    iconColor: entry.iconColor,
                    logo: entry.logo,
                }}
                aria-hidden
            />
            {/* The name truncates in a narrow rail, so the full value stays
                available on hover. */}
            <span className={styles.entityName} title={entry.name}>
                {entry.name}
            </span>
        </button>
        {isReverted && (
            <span className={styles.revertedStatus} aria-label="Reverted">
                <CheckIcon className={styles.revertedIcon} />
                <span className={styles.revertedLabel}>Reverted</span>
            </span>
        )}
        {/* These statuses are icon-only, so they need an explicit role for the
            accessible name to be announced. "Reverted" above keeps visible text
            instead. */}
        {!isReverted && isApplied && (
            <span
                className={classNames(styles.statusIcon, styles.statusApplied)}
                role="img"
                aria-label="Applied"
                title="Applied"
            >
                <CheckIcon />
            </span>
        )}
        {!isReverted && !isApplied && isApplying && (
            <span
                className={classNames(styles.statusIcon, styles.statusApplying)}
                role="img"
                aria-label="Applying"
                title="Applying"
            >
                <ClockIcon />
            </span>
        )}
    </div>
);
